import { Link } from "react-router-dom";
import { Gift } from "lucide-react";
import { Header } from "@/components/Header";
import { SEO } from "@/components/SEO";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO 
        title="Terms of Service" 
        description="Read the terms and conditions for using Shakatah split payment services."
        url="/terms-of-service"
      />
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

        <div className="prose prose-gray dark:prose-invert max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              By accessing or using Shakatah's split payment platform, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground">
              Shakatah provides a split payment solution that enables Shopify merchants to offer group purchasing options. Our platform facilitates the collection of payments from multiple participants for a single product purchase.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. Merchant Responsibilities</h2>
            <p className="text-muted-foreground mb-3">As a merchant using Shakatah, you agree to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Provide accurate business and product information</li>
              <li>Fulfill orders for completed split payments</li>
              <li>Comply with all applicable laws and Shopify's terms of service</li>
              <li>Maintain valid payment processing credentials</li>
              <li>Handle customer refunds and disputes in good faith</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Payment Terms</h2>
            <p className="text-muted-foreground mb-3">
              Split payments are processed through integrated payment gateways (Razorpay, Stripe, PayU). Shakatah charges a transaction fee based on your subscription plan:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Starter Plan: 3% per transaction</li>
              <li>Growth Plan: 2% per transaction</li>
              <li>Pro Plan: 1.5% per transaction</li>
              <li>Pay As You Go: 2% per transaction</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Cancellations and Refunds</h2>
            <p className="text-muted-foreground">
              If a split payment does not complete by the deadline, participants who have paid will receive gift vouchers for their contribution amount. Cancellation fees may apply as configured by the merchant.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Subscription and Billing</h2>
            <p className="text-muted-foreground">
              Subscription fees are billed monthly or annually based on your selected plan. You may cancel your subscription at any time, with access continuing until the end of your billing period.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              Shakatah is not liable for any indirect, incidental, or consequential damages arising from the use of our platform. Our total liability shall not exceed the fees paid by you in the preceding 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Termination</h2>
            <p className="text-muted-foreground">
              We reserve the right to suspend or terminate your account for violations of these terms, fraudulent activity, or non-payment of fees.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We may update these terms from time to time. Continued use of our services after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Contact</h2>
            <p className="text-muted-foreground">
              For questions about these Terms of Service, contact us at legal@shakatah.com.
            </p>
          </section>
        </div>
      </main>
      
      <footer className="border-t border-border bg-card/50">
        <div className="container py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-primary" />
              <span className="font-medium text-foreground">Shakatah</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/refund-policy" className="hover:text-foreground transition-colors">Refunds</Link>
              <a href="/#faq" className="hover:text-foreground transition-colors">FAQ</a>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
          <div className="mt-4 text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} Shakatah. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
